import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { CONTACT, LOCATIONS_LIVE } from "../../data";
import { submitBooking, formatApiError } from "../../lib/api";

export const Footer = () => {
  const [form, setForm] = useState({ name: "", phone: "", city: "", service: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await submitBooking(form);
      const msg = `*New Booking / Callback — CARFIX AUTO*%0A%0AName: ${form.name}%0APhone: ${form.phone}%0ACity: ${form.city || "-"}%0AService: ${form.service || "-"}`;
      window.open(`https://wa.me/${CONTACT.whatsapp}?text=${msg}`, "_blank");
      toast.success("Request received — a CARFIX advisor will call you shortly.");
      setForm({ name: "", phone: "", city: "", service: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-white/20 py-3 font-mono text-sm text-white placeholder:text-white/40 focus:border-[var(--volt)] focus:outline-none transition-colors duration-300";

  return (
    <footer
      id="contact"
      data-testid="footer"
      className="relative overflow-hidden bg-[#050505] pt-24 md:pt-36"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          {/* Big CTA */}
          <div className="lg:col-span-6">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--volt)]">
              / Book a bay
            </span>
            <h2 className="mt-6 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter text-white md:text-7xl">
              Bring us
              <br />
              <span className="text-stroke-volt">your car.</span>
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-white/60 md:text-lg">
              Tell us where you are and what your car needs. We&apos;ll match you to
              the nearest CARFIX bay and call you back within the hour.
            </p>

            <div className="mt-12 space-y-2 font-mono text-sm text-white/60">
              <div>
                <span className="text-white/40">E · </span>
                {CONTACT.email}
              </div>
              <div>
                <span className="text-white/40">P · </span>
                <a href={`tel:${CONTACT.phones[0].replace(/\s/g, "")}`} className="hover:text-[var(--volt)]">
                  {CONTACT.phones[0]}
                </a>
              </div>
              <div>
                <span className="text-white/40">P · </span>
                <a href={`tel:${CONTACT.phones[1].replace(/\s/g, "")}`} className="hover:text-[var(--volt)]">
                  {CONTACT.phones[1]}
                </a>
              </div>
              <div className="pt-2">
                <span className="text-white/40">Centres · </span>
                {LOCATIONS_LIVE.map((l) => l.city).join(" · ")} (Punjab)
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-6">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="grid grid-cols-1 gap-8 sm:grid-cols-2"
            >
              <input
                data-testid="contact-name"
                className={field}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                data-testid="contact-phone"
                className={field}
                placeholder="Phone number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <input
                data-testid="contact-city"
                className={field}
                placeholder="City"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
              <input
                data-testid="contact-service"
                className={field}
                placeholder="Service needed"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
              />
              <motion.button
                data-testid="contact-submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="group mt-2 flex items-center justify-center gap-3 rounded-full bg-[var(--volt)] px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black disabled:opacity-60 sm:col-span-2"
              >
                {loading ? "Sending…" : "Request Callback"}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </motion.button>
            </form>
          </div>
        </div>

        {/* Giant wordmark */}
        <div className="mt-24 border-t border-white/10 pt-10">
          <div className="font-display text-[16vw] font-black uppercase leading-none tracking-tighter text-stroke">
            CARFIX
          </div>
          <div className="mt-6 flex flex-col items-start justify-between gap-4 pb-10 font-mono text-xs uppercase tracking-wider text-white/40 md:flex-row md:items-center">
            <span>© {new Date().getFullYear()} Carfix Auto · Pan India</span>
            <span>German-Engineered Car Care</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
